import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Chart } from 'react-google-charts';
import { Icon } from 'semantic-ui-react';
import moment from 'moment';
import rgb2hex, { convertTimestampMilliseconds } from '../../../util/Util';
import polyglotUIComponents from '../../../i18n';
import {
  colorsScale,
  colorsModeUI,
  COLOR_SCALE_PRIMARY_COLORS,
  COLOR_SCALE_CHARTS_COLORS,
} from '../../../modeUI';
import { DATETIME_FORMAT } from '../../../constants';
import {
  getDateFormatGoogleCharts,
  getDateFormat,
  DATETIME_FORMAT_GOOGLE_CHARTS,
  getPointSize,
} from '../../constants';
import { convertData, getCoords } from './data';
import './index.css';
import { LineLabels } from './components/LineLabels';
import { getTimeTicks } from '../../util/getTimeTicks';
import { max, min } from 'lodash';

export const MIN_RANGE_VAXIS = 100; /* minimal cropping range of the vertical axis. */
export const PADDING_RANGE_VAXIS = 5; /* this values will be added to the min and max values */

/* SIZES CHART:
 **************************************************************************
 * LEGENDA *  WIDHT ONLY CHARTS                                           *
 * WIDTH   *                                                              *
 *         *                                                              *
 ************************************************************************* */

let lastZoom;
// let eventReady;
let zoomed = false;
let observer = new MutationObserver(() => {});

/**
 * LineChart
 * Display charts type LineChart
 * parameters (props)
 * @param string mode
 * @param array data = [{name, unit, data: [timestamp, val]}]
 * @param number width: chart's width including the size of the widthLegendeVaxis
 * @param number height: chart's height
 * @param number widthLegendeVaxis: legenda's height
 * @param number minDatetime: timestamp format: min value view port haxis
 * @param number maxDatetime: timestamp format: max value view port haxis
 */
export default class LineChart extends PureComponent {
  cssName = 'chart-linechart';
  chartType = 'LineChart';

  /**
   * Draw chart
   * @param object chart
   * @param object chartWrapper
   * @param object options
   * @param number minDatetime: timestamp format: min value view port haxis
   * @param number maxDatetime: timestamp format: max value view port haxi
   */
  static drawChart(chart, chartWrapper, options, minDatetime, maxDatetime) {
    if (minDatetime > 0 && maxDatetime > 0) {
      Object.assign(options.hAxis, {
        minValue: new Date(
          convertTimestampMilliseconds(moment(minDatetime * 1000).unix())
        ),
        maxValue: new Date(
          convertTimestampMilliseconds(moment(maxDatetime * 1000).unix())
        ),
      });
      Object.assign(options.hAxis.viewWindow, {
        min: new Date(
          convertTimestampMilliseconds(moment(minDatetime * 1000).unix())
        ),
        max: new Date(
          convertTimestampMilliseconds(moment(maxDatetime * 1000).unix())
        ),
      });
      chart.clearChart();
      chartWrapper.setOptions(options);
      chartWrapper.draw();
    }
  }

  constructor(props) {
    super(props);
    this.getWidth = this.getWidth.bind(this);
    this.getHeight = this.getHeight.bind(this);
    this.changeLegendeColors = this.changeLegendeColors.bind(this);
    this.getColorScaleCharts = this.getColorScaleCharts.bind(this);
    this.detectRightClick = this.detectRightClick.bind(this);
    this.handleSelectArea = this.handleSelectArea.bind(this);
    this.polyglot = polyglotUIComponents(props.language);
  }

  componentDidMount() {
    const { minDatetime, maxDatetime } = this.props;
    lastZoom = {
      min: moment.unix(minDatetime - 1).toDate(),
      max: moment.unix(maxDatetime - 1).toDate(),
    };
  }

  componentWillUnmount() {
    window.removeEventListener('contextmenu', this.detectRightClick.bind(this));
  }

  /**
   * Get color scale charts
   * @return array colorPalette
   */
  getColorScaleCharts() {
    const { mode, data } = this.props;
    let colorPalette = [];
    if (mode) {
      colorPalette = colorsScale(
        mode,
        data.length,
        data.length < 5 ? COLOR_SCALE_PRIMARY_COLORS : COLOR_SCALE_CHARTS_COLORS
      );
    }
    return colorPalette;
  }

  /**
   * Get width
   * @return width
   */
  getWidth() {
    const { width } = this.props;
    return width <= 0 ? window.innerWidth * 0.01 * 95 : width;
  }

  /**
   * Get height
   * @return height
   */
  getHeight() {
    const { height } = this.props;
    return !height ? window.innerHeight * 0.01 * 95 : height;
  }

  /**
   * Handle select area
   * @param object chart
   * @param object chartWrapper
   * @param object options
   * @param object eventSelect
   * @param array dataChart
   */
  handleSelectArea(chart, chartWrapper, options, dataChart) {
    const { callBackZoom, minDatetime, maxDatetime } = this.props;
    const zoom = getCoords(chart);
    const zoomMin = Math.max(moment(zoom.min).unix(), minDatetime || 0);
    const zoomMax = Math.min(moment(zoom.max).unix(), maxDatetime || Infinity);
    const lastZoomMin = moment(lastZoom.min).unix();
    const lastZoomMax = moment(lastZoom.max).unix();
    const distanceMin = Math.abs(zoomMin - lastZoomMin);
    const distanceMax = Math.abs(zoomMax - lastZoomMax);

    const outsideMin = moment(zoom.min).unix() < minDatetime;
    const outsideMax = moment(zoom.max).unix() > maxDatetime;
    // Why 10, the chart tome some time to render, 10 seconds its a good threshold
    if (distanceMin + distanceMax > 10) {
      // outside from searchFrame, need re-render
      if (outsideMax || outsideMin) {
        LineChart.drawChart(chart, chartWrapper, options, zoomMin, zoomMax);
      }
      lastZoom = {
        min: moment.unix(zoomMin).toDate(),
        max: moment.unix(zoomMax).toDate(),
      };
      zoomed = true;
      this.changeLegendeColors(dataChart[0]);
      callBackZoom && callBackZoom(moment.unix(zoomMin), moment.unix(zoomMax));
    }
  }

  /**
   * Change lengende colors
   * This function changes the color of the legende name of each chart using the
   * colors defined in the colorsPalette
   * @param array nameCharts
   */
  changeLegendeColors(nameCharts) {
    const { mode } = this.props;
    if (mode) {
      const colorsPalette = this.getColorScaleCharts();
      const findNameChart = (name) => {
        const foundIndex = nameCharts
          .slice(1, nameCharts.length + 1)
          .findIndex((n) => n === name);
        if (foundIndex >= 0) {
          return colorsPalette[foundIndex];
        }
        return false;
      };
      const chartHtml = document.getElementsByClassName(this.cssName);
      if (chartHtml && chartHtml.length > 0) {
        const texts = chartHtml[0].getElementsByTagName('text');
        for (let i = 0; i < texts.length; i += 1) {
          const found = findNameChart(texts.item(i).innerHTML);
          if (found) {
            texts.item(i).style.fill = found;
          }
        }
      }
    }
  }

  /**
   * Detect right click
   * @param object event
   * @param object chart
   * @param object dataChart
   * @param object options
   * @param object chartWrapper
   */
  detectRightClick(event, chart, dataChart, options, chartWrapper) {
    const { minDatetime, maxDatetime, callBackZoom } = this.props;
    if (event.button === 2 && zoomed) {
      const zoom = getCoords(chart);
      const distanceMin = Math.abs(moment(zoom.min).unix() - minDatetime);
      const distanceMax = Math.abs(moment(zoom.max).unix() - maxDatetime);
      if (distanceMin > 0 || distanceMax > 0) {
        lastZoom = {
          min: moment.unix(minDatetime - 1).toDate(),
          max: moment.unix(maxDatetime - 1).toDate(),
        };
        zoomed = false;
        callBackZoom(moment(minDatetime * 1000), moment(maxDatetime * 1000));
        this.changeLegendeColors(dataChart[0]);
        LineChart.drawChart(
          chart,
          chartWrapper,
          options,
          minDatetime,
          maxDatetime
        );
      }
    }
  }

  render() {
    const modeColors = colorsModeUI(this.props.mode);
    const {
      data,
      mode,
      chartArea,
      minDatetime,
      maxDatetime,
      zoom,
      labels,
      styleContainer = {
        backgroundColor: modeColors.CONTROL_UNIT_DETAILS_BACKGROUND_COLOR,
        color: modeColors.INFORMATION_COLOR,
      },
      ...otherProps
    } = this.props;
    let axisStyle = {};
    let axisTextStyle = {};
    let helpNoteStyle = {};

    /* Add color definition axis */
    axisTextStyle = {
      color: rgb2hex(modeColors.INFORMATION_COLOR),
    };
    axisStyle = {
      textStyle: axisTextStyle,
      titleTextStyle: axisTextStyle,
      gridlines: {
        color: rgb2hex(modeColors.CHART_AXES_COLOR),
      },
      baselineColor: rgb2hex(modeColors.CHART_AXES_COLOR),
    };
    helpNoteStyle = {
      color: modeColors.CHART_NOTE_COLOR,
    };
    const withLabelsHTML = labels.type === 'HTML';
    const widthChart = this.getWidth();
    const heightChart = this.getHeight();
    const { dataChart, series, vAxes } = convertData({
      dataset: data,
      axisStyle: axisStyle,
      width: widthChart,
      height: heightChart,
      withLabels: withLabelsHTML,
      maxVAxis: labels.maxVAxis,
    });
    /* date format */
    let dateFormat = getDateFormatGoogleCharts(DATETIME_FORMAT);
    if (dataChart && dataChart.length > 1 && dataChart[1].length > 0) {
      const dateMin = !minDatetime ? dataChart[1][0].getTime() : minDatetime;
      const dateMax = !maxDatetime
        ? dataChart[dataChart.length - 1][0].getTime()
        : maxDatetime;
      dateFormat = getDateFormatGoogleCharts(getDateFormat(dateMin, dateMax));
    }

    /* Calculation Sizes: width and height */
    const styleChart = {
      width: widthChart,
      height: this.getHeight(),
    };
    if (dataChart && dataChart.length > 0) {
      /* options charts */
      const options = {
        fontSize: 12,
        pointSize: getPointSize(dataChart.length, widthChart),
        pointShape: { type: 'circle' },
        hAxis: {
          format: dateFormat /* date time used in the haxis */,
          slantedText: true,
          textStyle: axisTextStyle,
          titleTextStyle: axisStyle,
          //ticks: [0, 5, 10, 15, 25]
          ticks: getTimeTicks(minDatetime, maxDatetime, this.getWidth()).map(
            (item) => moment.unix(item.timeStamp).toDate()
          ),
          textPosition: withLabelsHTML ? 'none' : undefined,
        },
        focusTarget: 'category',
        series: series,
        vAxes: vAxes,
        legend: {
          position: 'top',
        },
      };
      if (zoom) {
        Object.assign(options, {
          explorer: {
            actions: ['dragToZoom', 'rightClickToReset'],
            keepInBounds: true,
            axis: 'horizontal',
            maxZoomIn: 10.0,
          },
        });
      } else {
        Object.assign(options, { explorer: null });
      }

      /* Width legende vaxis */
      if (withLabelsHTML) {
        Object.assign(options, {
          chartArea,
        });
      }
      /* color definition */
      if (mode) {
        const modeColors = colorsModeUI(mode);
        const colorPalette = this.getColorScaleCharts();
        /* color horizontal axis */
        Object.assign(options.hAxis, {
          color: rgb2hex(modeColors.CHART_AXES_COLOR),
          gridlines: {
            color: rgb2hex(modeColors.CHART_AXES_COLOR),
          },
          baselineColor: rgb2hex(modeColors.CHART_AXES_COLOR),
        });
        /* color legenda */
        Object.assign(options.legend, {
          textStyle: {
            color: rgb2hex(modeColors.INFORMATION_COLOR),
          },
        });
        /* color charts */
        Object.assign(options, {
          colors: colorPalette,
          backgroundColor: rgb2hex(
            modeColors.CONTROL_UNIT_DETAILS_BACKGROUND_COLOR
          ),
        });
      }

      if (dataChart.length > 1) {
        const allValues = dataChart.slice(1).flatMap((d) =>
          Object.values(d)
            .slice(1)
            .filter((d) => !!d)
            .map((d) => d.v)
        );

        const minValueVAxis = min(allValues);
        const maxValueVAxis = max(allValues);
        if (Math.abs(maxValueVAxis - minValueVAxis) > MIN_RANGE_VAXIS) {
          Object.assign(options, {
            vAxis: {
              viewWindow: {
                min: minValueVAxis - PADDING_RANGE_VAXIS,
                max: maxValueVAxis + PADDING_RANGE_VAXIS,
              },
            },
          });
        }
      }
      if (minDatetime || maxDatetime) {
        Object.assign(options.hAxis, { viewWindow: {} });
        if (minDatetime) {
          /* minDatetime = format timestamp */
          const minValueDatetime = new Date(
            convertTimestampMilliseconds(minDatetime)
          );
          Object.assign(options.hAxis.viewWindow, { min: minValueDatetime });
        }
        if (maxDatetime) {
          /* maxDatetime = format timestamp */
          const maxValueDatetime = new Date(
            convertTimestampMilliseconds(maxDatetime)
          );
          Object.assign(options.hAxis.viewWindow, { max: maxValueDatetime });
        }
      }
      const chartEvents = [
        {
          eventName: 'ready',
          callback: ({ chartWrapper }) => {
            const chart = chartWrapper.getChart();
            // Observe zoom
            observer.disconnect();
            observer = new MutationObserver((...events) => {
              this.handleSelectArea(chart, chartWrapper, options, dataChart);
            });
            observer.observe(chart.container, {
              childList: true,
              subtree: true,
            });
            const eventReady = google.visualization.events.addListener(
              chart,
              'ready',
              () => {
                this.changeLegendeColors(dataChart[0]);
                google.visualization.events.removeListener(eventReady);
              }
            );
            const elem = document.getElementsByClassName(this.cssName);
            if (elem[0] && elem[0].addEventListener) {
              elem[0].addEventListener(
                'contextmenu',
                (event) =>
                  this.detectRightClick(
                    event,
                    chart,
                    dataChart,
                    options,
                    chartWrapper
                  ),
                false
              );
            }
          },
        },
      ];
      return (
        <div className="LineChart" style={styleContainer}>
          {zoom && (
            <div style={helpNoteStyle} id="helpNoteZoom" className="help-text">
              <Icon name="info circle" />
              {`${this.polyglot.t('charts.zoom_in')} ${this.polyglot.t(
                'charts.reset_zoom'
              )}`}
            </div>
          )}
          <div className={`LineChart__combo-labels-chart`}>
            {withLabelsHTML && (
              <LineLabels vAxes={vAxes} mode={mode} width={labels.widthVAxis} />
            )}
            <Chart
              className={this.cssName}
              chartType={this.chartType}
              data={dataChart}
              style={styleChart}
              formatters={[
                {
                  type: 'DateFormat',
                  column: 0,
                  options: {
                    pattern:
                      DATETIME_FORMAT_GOOGLE_CHARTS /* date time format used in the legenda */,
                  },
                },
              ]}
              options={options}
              chartEvents={chartEvents}
              {...otherProps}
            />
          </div>
        </div>
      );
    }
    return null;
  }
}

LineChart.propTypes = {
  mode: PropTypes.string,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      unit: PropTypes.string.isRequired,
      data: PropTypes.arrayOf(
        PropTypes.shape({
          timestamp: PropTypes.number.isRequired,
          val: PropTypes.number.isRequired,
        })
      ).isRequired,
    }).isRequired
  ).isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  widthLegendeVaxis: PropTypes.number,
  chartArea: PropTypes.any,
  minDatetime: PropTypes.number /* timestamp format */,
  maxDatetime: PropTypes.number /* timestamp format */,
  callBackZoom: PropTypes.func,
  language: PropTypes.string,
  zoom: PropTypes.bool.isRequired,
  styleContainer: PropTypes.any,
  labels: PropTypes.shape({
    type: PropTypes.string.isRequired,
    maxVAxis: PropTypes.number.isRequired,
    widthVAxis: PropTypes.number,
  }),
};

LineChart.defaultProps = {
  mode: null,
  width: null,
  height: null,
  widthLegendeVaxis: null,
  labels: {
    type: 'HTML',
    maxVAxis: 5,
    widthVAxis: undefined, // 60*5, if you define it, recommend use <maxVAxis> * 60, because each vAxis has 59px
  },
  chartArea: {
    left: 0,
    top: 20, // legend
    bottom: 0,
    right: 0,
    width: '100%',
  },
  minDatetime: null,
  maxDatetime: null,
  callBackZoom: null,
  language: null,
};
